import { useMemo, useRef } from 'react';

import { Box } from '@chakra-ui/react';
import { Card, CardHeader, CardTitle, LineChart } from '@mesh.ai/rabbit';
import { metricTypeTransformers } from 'Deer/hooks/dataHooks/metrics/utils/overviewMetricsUtils';

import { Download } from 'assets/assetsV1/iconComponents';
import noInsights from 'assets/assetsV1/illustrations/deerIllustrations/noInsights.svg';

import { Button } from 'uiToolkitV1/Button';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { ExpandableContainerProvider } from 'uiToolkitV1/ExpandableContainerProvider';

import { ChartErrorScreen, ChartLoading } from '../GraphSection/GraphLoadingSections';
import { useGetDisciplinaryActionsViolationCountGraphData } from './dataHooks';

export const DisciplinaryActionViolationCountChart = ({ dateRange, companyId = [], location = [] }) => {
  const { data, isLoading, isError } = useGetDisciplinaryActionsViolationCountGraphData({
    cohortId: -1,
    reportType: 'DISCIPLINARY_ACTION_NOTES',
    rowGroupCols: [],
    pivotCols: [],
    pivotMode: false,
    filterModel: {},
    baseFilter: { ...dateRange, companyId, location },
    limit: null,
    sortModel: [],
  });

  const chartRef = useRef(null);

  const chartData = useMemo(() => {
    if (!data?.entity?.metrics) return [];

    const transformedData: { [key: string]: unknown } = {};
    data?.entity?.metrics?.forEach((metric) => {
      const metricType = metric.metric;
      const dataTransformer = metricTypeTransformers[metricType];
      if (dataTransformer) transformedData[metric.metric] = dataTransformer(metric);
    });

    return transformedData ? transformedData.EMPLOYEES_WITH_VIOLATIONS_PERCENTAGE : {};
  }, [data]);

  if (isLoading) {
    return <ChartLoading width="100%" />;
  }

  if (isError) {
    return <ChartErrorScreen width="100%" />;
  }

  const handleDownload = () => {
    if (chartRef.current) {
      chartRef.current.download();
    }
  };

  const hasNoData = !chartData || chartData?.length === 0;

  if (hasNoData) {
    return (
      <Card style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', background: 'white' }}>
        <CardHeader>
          <Box h="100%" w="100%" display="flex" justifyContent="center" alignItems="center">
            <EmptyScreen
              illustrationSrc={noInsights}
              heading="Not enough data to show"
              isSmallTextSize
              description="No data found for the selected filters"
            />
          </Box>
        </CardHeader>
      </Card>
    );
  }
  return (
    <ExpandableContainerProvider
      id="disciplinaryActionExpandableChart"
      isParentDeterminesHeight
      toggleBtnBoxProps={{ top: '4px !important', right: '4px !important' }}
    >
      <Card style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' }}>
        <CardHeader>
          <CardTitle>Employees by Violation Count (%)</CardTitle>
          <Button
            position="absolute"
            top="4px"
            right="38px"
            variant="tertiary"
            EndIcon={Download}
            onClick={handleDownload}
            isDisabled={!chartData}
          />
        </CardHeader>
        <Box w="100%" h="100%" overflow="hidden">
          <LineChart
            ref={chartRef}
            // @ts-expect-error unknown type
            nodes={chartData.nodes}
            // @ts-expect-error unknown type
            xAxisValues={chartData.xAxis}
            // @ts-expect-error unknown type
            series={chartData.series}
            xAxisLabel="No. of violations"
            yAxisLabel="Distinct count of  Employees"
            yAxisUnitSymbol={{ position: 'post', symbol: '%' }}
            yAxisDomain={[0, 100]}
            tooltipTitle="Employees by Violation Count (%)"
            mode="mini"
            maxNodesInMiniMode={5}
          />
        </Box>
      </Card>
    </ExpandableContainerProvider>
  );
};
